// Repositórios de dados — uma camada fina por tabela do Supabase.
// Cada repositório expõe list/insert/update/remove e converte entre a LINHA
// do banco (snake_case, datas ISO) e o OBJETO usado na UI (camelCase, datas BR).
//
// Convenção de datas:
//   * No banco: colunas `date` em ISO "aaaa-mm-dd".
//   * Na UI (igual ao app legado): strings "dd/mm/aaaa".
import { supabase } from '../lib/supabase.js'
import { markWrite } from '../lib/recent-writes.js'
import { isValidBRDate } from '../lib/format.js'
import { rastrear } from '../lib/save-status.js'

// UUID "nulo" — usado como filtro no clearAll (deleta tudo, já que nenhum
// registro real tem esse id). O Supabase exige um filtro em delete.
const NIL_UUID = '00000000-0000-0000-0000-000000000000'

// Executa uma query de ESCRITA contabilizando no indicador de salvamento
// (rastrear) e propagando o erro do Supabase como exceção.
const exec = q => rastrear(q.then(({ data, error }) => { if (error) throw error; return data }))

/* ============ Conversão de datas (ISO ⇄ BR) ============ */

// "aaaa-mm-dd" (ou ISO completo) → "dd/mm/aaaa". Vazio/nulo → ''.
function isoParaBR(iso) {
  if (!iso) return ''
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  return `${m[3]}/${m[2]}/${m[1]}`
}

// "dd/mm/aaaa" (ou dd/mm/aa) → "aaaa-mm-dd". Vazio/inválido → null (coluna date).
function brParaISO(br) {
  if (!br) return null
  const m = String(br).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (!m) return null
  let y = +m[3]
  if (y < 100) y += 2000
  const mm = String(+m[2]).padStart(2, '0')
  const dd = String(+m[1]).padStart(2, '0')
  // rejeita datas fora de faixa (ex.: 61/65/4654) — evita erro no Postgres.
  if (!isValidBRDate(`${dd}/${mm}/${y}`)) return null
  return `${y}-${mm}-${dd}`
}

/* ============ Fábrica genérica de repositório ============ */
// Centraliza o tratamento de erros e a forma das chamadas para reduzir repetição.
function criarRepo(tabela, paraObjeto, paraLinha) {
  return {
    tabela,
    paraObjeto,

    // Lista ordenada por posicao, depois created_at (regra de carga do app).
    async list() {
      const { data, error } = await supabase
        .from(tabela)
        .select('*')
        .order('posicao', { ascending: true })
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data || []).map(paraObjeto)
    },

    // Insere e devolve o objeto já com o id. Aceita id gerado no cliente
    // (crypto.randomUUID): nesse caso marca ANTES do await, pois o eco do
    // realtime pode chegar antes da resposta HTTP — assim o store o ignora.
    async insert(obj) {
      const linha = paraLinha(obj)
      if (obj && obj.id) {
        linha.id = obj.id
        markWrite(obj.id)
      }
      const data = await exec(supabase.from(tabela).insert(linha).select().single())
      markWrite(data.id)
      return paraObjeto(data)
    },

    // Insere vários de uma vez (usado na importação). Devolve os objetos.
    async insertMany(objs) {
      if (!objs || !objs.length) return []
      const data = await exec(supabase.from(tabela).insert(objs.map(paraLinha)).select())
      ;(data || []).forEach(d => markWrite(d.id))
      return (data || []).map(paraObjeto)
    },

    // Atualiza apenas as colunas alteradas (patch parcial) por id.
    // markWrite ANTES do await: o eco do realtime pode chegar antes da resposta.
    async update(id, patch) {
      markWrite(id)
      await exec(supabase.from(tabela).update(patch).eq('id', id))
    },

    // Remove por id.
    async remove(id) {
      markWrite(id)
      await exec(supabase.from(tabela).delete().eq('id', id))
    },

    // Apaga TODAS as linhas da tabela (usado na importação que substitui).
    async clearAll() {
      await exec(supabase.from(tabela).delete().neq('id', NIL_UUID))
    }
  }
}

/* ============ OFERTAS ============ */
// Legado: { id(=codigo), gestor, data, ticket, ic(=ic_limiar), cpa(=cpa_alvo) }.
// A UI mantém os nomes legados (codigo, ic, cpa) para preservar a lógica.
function ofertaParaObjeto(r) {
  return {
    id: r.id,
    codigo: r.codigo || '',
    gestor: r.gestor || '',
    data: isoParaBR(r.data_oferta),
    ticket: r.ticket == null ? null : Number(r.ticket),
    ic: r.ic_limiar == null ? null : Number(r.ic_limiar),
    cpa: r.cpa_alvo == null ? null : Number(r.cpa_alvo),
    ativa: r.ativa !== false,
    posicao: r.posicao ?? 0,
    criativos: []
  }
}
function ofertaParaLinha(o) {
  return {
    codigo: o.codigo || '',
    gestor: o.gestor || '',
    data_oferta: brParaISO(o.data),
    ticket: o.ticket == null || o.ticket === '' ? 0 : Number(o.ticket),
    ic_limiar: o.ic == null || o.ic === '' ? 0 : Number(o.ic),
    cpa_alvo: o.cpa == null || o.cpa === '' ? 0 : Number(o.cpa),
    ativa: o.ativa !== false,
    posicao: o.posicao ?? 0
  }
}
// Mapa de campo de oferta (UI) → coluna do banco, usado nos updates pontuais.
export const OFERTA_COL = {
  codigo: 'codigo',
  gestor: 'gestor',
  data: 'data_oferta',
  ticket: 'ticket',
  ic: 'ic_limiar',
  cpa: 'cpa_alvo',
  ativa: 'ativa',
  posicao: 'posicao'
}
// Converte um valor de campo de oferta no formato esperado pela coluna.
export function ofertaValorColuna(campo, valor) {
  if (campo === 'data') return brParaISO(valor)
  if (campo === 'ticket' || campo === 'ic' || campo === 'cpa') {
    const n = valor == null || valor === '' ? 0 : Number(valor)
    return isNaN(n) ? 0 : n
  }
  return valor ?? ''
}
export const ofertasRepo = criarRepo('ofertas', ofertaParaObjeto, ofertaParaLinha)

/* ============ CRIATIVOS ============ */
// Legado: linha array [data, conta, status, criativo, gestor, gasto, ic, vendas].
function criativoParaObjeto(r) {
  return {
    id: r.id,
    oferta_id: r.oferta_id,
    data: isoParaBR(r.data_criativo),
    conta: r.conta || '',
    status: r.status || 'ATIVA',
    criativo: r.criativo || '',
    gestor: r.gestor || '',
    gasto: r.gasto == null ? null : Number(r.gasto),
    ic: r.ic == null ? null : Number(r.ic),
    vendas: r.vendas == null ? null : Number(r.vendas),
    posicao: r.posicao ?? 0
  }
}
function criativoParaLinha(c) {
  return {
    oferta_id: c.oferta_id,
    data_criativo: brParaISO(c.data),
    conta: c.conta || '',
    status: c.status || 'ATIVA',
    criativo: c.criativo || '',
    gestor: c.gestor || '',
    gasto: c.gasto == null || c.gasto === '' ? 0 : Number(c.gasto),
    ic: c.ic == null || c.ic === '' ? 0 : Number(c.ic),
    vendas: c.vendas == null || c.vendas === '' ? 0 : Number(c.vendas),
    posicao: c.posicao ?? 0
  }
}
export const CRIATIVO_COL = {
  data: 'data_criativo',
  conta: 'conta',
  status: 'status',
  criativo: 'criativo',
  gestor: 'gestor',
  gasto: 'gasto',
  ic: 'ic',
  vendas: 'vendas',
  posicao: 'posicao'
}
export function criativoValorColuna(campo, valor) {
  if (campo === 'data') return brParaISO(valor)
  if (campo === 'gasto' || campo === 'ic' || campo === 'vendas') {
    const n = valor == null || valor === '' ? 0 : Number(valor)
    return isNaN(n) ? 0 : n
  }
  return valor ?? ''
}
export const criativosRepo = criarRepo('criativos', criativoParaObjeto, criativoParaLinha)
// Exporta o conversor de objeto para o store montar a árvore oferta→criativos.
export { criativoParaObjeto }

/* ============ CONTAS ============ */
function contaParaObjeto(r) {
  return {
    id: r.id,
    conta: r.conta || '',
    bm: r.bm || '',
    tipo: r.tipo || 'Normal',
    offer: r.offer || '',
    gestor: r.gestor || '',
    situacao: r.situacao || '',
    qualidade: r.qualidade || '',
    status: r.status || 'ATIVO',
    data: isoParaBR(r.data),
    observacoes: r.observacoes || '',
    posicao: r.posicao ?? 0
  }
}
function contaParaLinha(c) {
  return {
    conta: c.conta || '',
    bm: c.bm || '',
    tipo: c.tipo || 'Normal',
    offer: c.offer || '',
    gestor: c.gestor || '',
    situacao: c.situacao || '',
    qualidade: c.qualidade || '',
    status: c.status || 'ATIVO',
    data: brParaISO(c.data),
    observacoes: c.observacoes || '',
    posicao: c.posicao ?? 0
  }
}
export const CONTA_COL = {
  conta: 'conta',
  bm: 'bm',
  tipo: 'tipo',
  offer: 'offer',
  gestor: 'gestor',
  situacao: 'situacao',
  qualidade: 'qualidade',
  status: 'status',
  data: 'data',
  observacoes: 'observacoes',
  posicao: 'posicao'
}
export function contaValorColuna(campo, valor) {
  if (campo === 'data') return brParaISO(valor)
  return valor ?? ''
}
export const contasRepo = criarRepo('contas', contaParaObjeto, contaParaLinha)

/* ============ PERFIS ============ */
function perfilParaObjeto(r) {
  return {
    id: r.id,
    data: isoParaBR(r.data),
    squad: r.squad || '',
    gestor: r.gestor || '',
    login_fb: r.login_fb || '',
    email: r.email || '',
    senha_fb: r.senha_fb || '',
    senha_email: r.senha_email || '',
    fa_2: r.fa_2 || '',
    conta_recuperacao: r.conta_recuperacao || '',
    links: r.links || '',
    posicao: r.posicao ?? 0
  }
}
function perfilParaLinha(p) {
  return {
    data: brParaISO(p.data),
    squad: p.squad || '',
    gestor: p.gestor || '',
    login_fb: p.login_fb || '',
    email: p.email || '',
    senha_fb: p.senha_fb || '',
    senha_email: p.senha_email || '',
    fa_2: p.fa_2 || '',
    conta_recuperacao: p.conta_recuperacao || '',
    links: p.links || '',
    posicao: p.posicao ?? 0
  }
}
export const PERFIL_COL = {
  data: 'data',
  squad: 'squad',
  gestor: 'gestor',
  login_fb: 'login_fb',
  email: 'email',
  senha_fb: 'senha_fb',
  senha_email: 'senha_email',
  fa_2: 'fa_2',
  conta_recuperacao: 'conta_recuperacao',
  links: 'links',
  posicao: 'posicao'
}
export function perfilValorColuna(campo, valor) {
  if (campo === 'data') return brParaISO(valor)
  return valor ?? ''
}
export const perfisRepo = criarRepo('perfis', perfilParaObjeto, perfilParaLinha)

/* ============ PROXYS ============ */
function proxyParaObjeto(r) {
  return {
    id: r.id,
    nome: r.nome || '',
    host: r.host || '',
    porta: r.porta || '',
    usuario: r.usuario || '',
    senha: r.senha || '',
    gestor: r.gestor || '',
    status: r.status || 'Ativa',
    posicao: r.posicao ?? 0
  }
}
function proxyParaLinha(p) {
  return {
    nome: p.nome || '',
    host: p.host || '',
    porta: p.porta || '',
    usuario: p.usuario || '',
    senha: p.senha || '',
    gestor: p.gestor || '',
    status: p.status || 'Ativa',
    posicao: p.posicao ?? 0
  }
}
export const proxysRepo = criarRepo('proxys', proxyParaObjeto, proxyParaLinha)

/* ============ DIARIAS ============ */
function diariaParaObjeto(r) {
  return {
    id: r.id,
    gestor: r.gestor || '',
    data: isoParaBR(r.data),
    titulo: r.titulo || '',
    status: r.status || 'A fazer',
    oferta: r.oferta || '',
    contas: r.contas || '',
    qtd: r.qtd || '',
    estrutura: r.estrutura || '',
    orcamento: r.orcamento || '',
    nicho: r.nicho || '',
    publico: r.publico || '',
    livre: r.livre || '',
    posicao: r.posicao ?? 0
  }
}
function diariaParaLinha(d) {
  return {
    gestor: d.gestor || '',
    data: brParaISO(d.data),
    titulo: d.titulo || '',
    status: d.status || 'A fazer',
    oferta: d.oferta || '',
    contas: d.contas || '',
    qtd: d.qtd || '',
    estrutura: d.estrutura || '',
    orcamento: d.orcamento || '',
    nicho: d.nicho || '',
    publico: d.publico || '',
    livre: d.livre || '',
    posicao: d.posicao ?? 0
  }
}
export const DIARIA_COL = {
  gestor: 'gestor',
  data: 'data',
  titulo: 'titulo',
  status: 'status',
  oferta: 'oferta',
  contas: 'contas',
  qtd: 'qtd',
  estrutura: 'estrutura',
  orcamento: 'orcamento',
  nicho: 'nicho',
  publico: 'publico',
  livre: 'livre',
  posicao: 'posicao'
}
export function diariaValorColuna(campo, valor) {
  if (campo === 'data') return brParaISO(valor)
  return valor ?? ''
}
export const diariasRepo = criarRepo('diarias', diariaParaObjeto, diariaParaLinha)

/* ============ DEMANDAS_STATUS ============ */
// Chave lógica conta|offer (unique no banco). Usa upsert para criar/atualizar.
function demandaParaObjeto(r) {
  return {
    id: r.id,
    conta: r.conta || '',
    offer: r.offer || '',
    status: r.status || 'A fazer'
  }
}
export const demandasRepo = {
  tabela: 'demandas_status',
  paraObjeto: demandaParaObjeto,
  async list() {
    const { data, error } = await supabase
      .from('demandas_status')
      .select('*')
      .order('created_at', { ascending: true })
    if (error) throw error
    return (data || []).map(demandaParaObjeto)
  },
  // Grava o status de uma combinação conta|offer (cria ou atualiza).
  async upsertStatus(conta, offer, status) {
    const data = await exec(
      supabase
        .from('demandas_status')
        .upsert({ conta, offer, status }, { onConflict: 'conta,offer' })
        .select()
        .single()
    )
    markWrite(data.id)
    return demandaParaObjeto(data)
  }
}
